import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { endpointOidcUserQueryKeys, GoogleIdP, googleIdPQueryKeys, hostQueryKeys } from '#api';
import type { GoogleIdPConnection } from '#dn-types/google-idp.ts';

export const googleIdPConnectionQueryOptions = queryOptions({
  queryKey: googleIdPQueryKeys.connection(),
  queryFn: ({ signal }) => GoogleIdP.getGoogleIdPConnection({ signal }),
});

/**
 * Not a suspense query on purpose: the card sits on the provider edit page, and a Google outage must
 * not take the whole page down with it.
 */
export function useGoogleIdPConnectionQuery({ enabled }: { enabled: boolean }) {
  return useQuery({ ...googleIdPConnectionQueryOptions, enabled });
}

export const googleIdPProvidersQueryOptions = queryOptions({
  queryKey: googleIdPQueryKeys.providers(),
  queryFn: ({ signal }) => GoogleIdP.getGoogleIdPProviders({ signal }),
});

/**
 * {@link useIsGoogleIdPProvider} is whether an endpoint SSO provider signs users in with Google, which is what makes
 * a Google Workspace directory connectable from it. The api decides, so the rule lives in one place. Not a suspense
 * query, like the connection: the provider page must not wait on it.
 */
export function useIsGoogleIdPProvider(providerId: string, { enabled }: { enabled: boolean }) {
  const { data } = useQuery({ ...googleIdPProvidersQueryOptions, enabled });
  return Boolean(data?.data.some((provider) => provider.id === providerId));
}

function useSetConnection() {
  const queryClient = useQueryClient();
  return (connection: GoogleIdPConnection | null) => {
    queryClient.setQueryData(googleIdPConnectionQueryOptions.queryKey, { data: connection });
  };
}

export function useStartGoogleIdP() {
  return useMutation({ mutationFn: GoogleIdP.startGoogleIdP });
}

export function useVerifyGoogleIdP() {
  const setConnection = useSetConnection();
  return useMutation({
    mutationFn: GoogleIdP.verifyGoogleIdP,
    onSuccess: setConnection,
  });
}

export function useTestGoogleIdP() {
  const queryClient = useQueryClient();
  const setConnection = useSetConnection();
  return useMutation({
    mutationFn: GoogleIdP.testGoogleIdP,
    onSuccess: setConnection,
    // A failed test is still recorded on the connection, so re-read it to show the new health.
    onError: () => queryClient.invalidateQueries(googleIdPConnectionQueryOptions),
  });
}

export function useDeleteGoogleIdP() {
  const setConnection = useSetConnection();
  return useMutation({
    mutationFn: GoogleIdP.deleteGoogleIdP,
    onSuccess: () => {
      setConnection(null);
    },
  });
}

/** Every sync, dry run or not, records the connection's health, so re-read it either way. */
export function useGoogleIdPSyncDryRun() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => GoogleIdP.runGoogleIdPSync({ apply: false }),
    onSettled: () => queryClient.invalidateQueries(googleIdPConnectionQueryOptions),
  });
}

export function useGoogleIdPSync() {
  const queryClient = useQueryClient();
  return useMutation({
    // maxDeprovisions raises the deprovisioning limit for one run, after the admin has reviewed the users.
    mutationFn: (variables: GoogleIdP.StartGoogleIdPSyncVariables) =>
      GoogleIdP.runGoogleIdPSync({ apply: true, ...variables }),
    // Even a failed run may have blocked, unblocked or deleted hosts before it stopped.
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries(googleIdPConnectionQueryOptions),
        queryClient.invalidateQueries({ queryKey: endpointOidcUserQueryKeys.all() }),
        queryClient.invalidateQueries({ queryKey: hostQueryKeys.all() }),
      ]),
  });
}
